import { signInWithPopup, signInWithRedirect, getRedirectResult, signOut, getAdditionalUserInfo } from 'firebase/auth';
import { auth } from '../firebase';
import { track, EVENTS } from './analytics';
import { signinEntry } from '../content/landingIds';
import { heldAttribution } from './attribution';
import { clearIdleActivity } from '../utils/sessionActivity';
import { STORED_KEY as EMAIL_LINK_STORED_KEY, emailSignInAvailable } from './emailLinkSignIn';
import {
  BACK_CODE, CANCELLED_CODES, IN_APP_CODE, LEFT_CODE, LOST_CODE, POPUP_UNSEEN_CODE, REASONS, clearSignInPending,
  endSignInAttempt, failureReason, inAppBrowserName, isIOS, loadedPath, markSignInPending, pageLoadType,
  pendingProvider, signInAttemptProps, signInPlatform, takeLostSignIn,
} from './signInTrouble';
import { PROVIDERS, methodForFirebaseId, providerName } from './signInProviders';
import { ACCOUNT_EXISTS_CODE, clearPendingLink, stashPendingLink } from './signInLink';
import { markSecondAccountHint } from './secondAccountHint';
import { heldMomentEntry } from './signupMoments';

// The page is about to leave for Google; it has not left yet. A network
// failure HERE is Firebase's first call to Google (the project-config read)
// failing before any navigation, so this browser cannot reach Google's
// sign-in at all (#3125). While the device says it is online that is a block
// (a filtering network, an extension, a datacenter browser), not a dropped
// connection, and the student needs different words. Tagged on the error so
// the notice and the analytics `reason` read the same thing.
async function startRedirect(providerId = 'google') {
  try {
    await signInWithRedirect(auth, PROVIDERS[providerId].make());
  } catch (error) {
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    if (error?.code === 'auth/network-request-failed' && online) {
      error.reason = REASONS.GOOGLE_UNREACHABLE;
    }
    throw error;
  }
}

// The acquisition-conversion analytics, fired identically for popup and
// redirect so Google Ads' `sign_up` key event never depends on the method.
// Firebase's own isNewUser flag is the authoritative signal (no profile-read
// heuristics). Fired under our taxonomy name AND GA4's standard `sign_up`.
// `method` (#5154): the provider the sign-in finished with, read from the
// result itself so a redirect's return counts under the provider it went to.
// `relay`: an Apple Hide My Email address (a boolean, never the address).
const RELAY_DOMAIN = '@privaterelay.appleid.com';
export const isRelayAddress = (email) => String(email || '').trim().toLowerCase().endsWith(RELAY_DOMAIN);

function resultMethod(result) {
  return methodForFirebaseId(result?.providerId || getAdditionalUserInfo(result)?.providerId);
}

function trackSignInResult(result) {
  try {
    const method = resultMethod(result);
    const relay = method === 'apple' ? { relay: isRelayAddress(result?.user?.email) } : {};
    const platform = signInPlatform();
    // First-touch landing (which video/paper + utm/gclid) rides on the
    // conversion so funnels can answer "which public proof converted."
    const attr = heldAttribution();
    // The attempt (#5084) is read before it ends: a later sign-in in this
    // tab is a new one.
    const attempt = signInAttemptProps();
    endSignInAttempt();
    // #5195: a sign-up moment's entry, from the record its button wrote.
    const moment = heldMomentEntry();
    const entry = moment ? { entry: moment } : {};
    track(EVENTS.SIGNIN_COMPLETED, { method, platform, ...entry, ...relay, ...attr, ...attempt });
    if (getAdditionalUserInfo(result)?.isNewUser) {
      track(EVENTS.SIGNED_UP, { method, ...entry, ...attr });
      track(EVENTS.GA4_SIGN_UP, { method, ...attr });
      // A new Apple account with a relay address, or a new Facebook
      // account, may be a returning student's second account (#5154).
      if (method === 'facebook' || relay.relay) markSecondAccountHint(method, result?.user?.uid);
    }
  } catch { /* analytics must never break sign-in */ }
}

// WHICH FLOW, per device (#2457, 2026-09-29 — reverses the iOS half of
// #1100's popup-first):
//   - iOS (every iOS browser is WebKit) → the full-page REDIRECT. Popup-first
//     cut iOS Safari completion from 12 of 16 (redirect, to Jul 29) to 22 of
//     56: the popup opens as a new tab and in 29 iOS starts the opener fired
//     $pageleave within 30s and never heard back. #1100 left the redirect
//     for Firebase's "missing initial state" page, but that trap needs the
//     auth handler on another origin; authDomain has been this site's own
//     domain since Jul 10, so the stash is first-party and stays in the tab.
//     A redirect that still comes back empty is caught on return by
//     detectLostSignIn() and shown, never silent.
//   - Everywhere else → POPUP first; the redirect is the escape hatch for a
//     blocked popup (2026-07-30) and Chrome 142's auth/internal-error (#2032).
//   - A known in-app browser (LinkedIn, Instagram, …) → no attempt on the
//     first tap: every LinkedIn start since Aug 9 ended popup-blocked →
//     redirect → auth/network-request-failed. The caller shows how to open
//     the page in a real browser; a second tap tries anyway, in case the
//     user-agent match is wrong.
// `useRedirectOnMobile: false` (PricingPage → Stripe) forbids every
// navigation, since a full-page hop would break the caller's inline flow.
const POPUP_BLOCKED = [
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
];

// One warning per page life, then the user's second tap is their call.
let inAppWarned = false;
export const _resetInAppWarning = () => { inAppWarned = false; };

// What a failure event carries besides the code (#2032): both Chrome 142
// sessions recorded only `auth/internal-error`, and the message that would
// have said WHY was never captured. Firebase's customData is NOT safe to
// forward: its tagged errors nest the student's email, phone number, the
// Google account id and OAuth tokens under _tokenResponse / _serverResponse
// (reviewer, #2034). An allowlist of scalar keys is the only shape that
// cannot leak — never "everything minus one key", never a byte cap.
const CUSTOM_DATA_KEYS = ['appName', 'tenantId', 'operationType'];

function failureDetail(error) {
  const detail = { message: String(error?.message || '').slice(0, 200) };
  const data = error?.customData;
  if (data && typeof data === 'object') {
    const safe = {};
    for (const key of CUSTOM_DATA_KEYS) {
      const v = data[key];
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') safe[key] = v;
    }
    if (Object.keys(safe).length) detail.custom = JSON.stringify(safe);
  }
  return detail;
}

// One sign-in for every provider (#5154): the same flow rule Google has
// always had (redirect on iOS, popup elsewhere, popup into checkout when the
// caller forbids navigation), the same events with the provider as `method`.
// `position` (1-3 among the drawn buttons) rides the start so the Desk can
// read whether the order matters (design spec section 4).
export const signInWithProvider = async (providerId = 'google', { useRedirectOnMobile = true, entry, position } = {}) => {
  const provider = PROVIDERS[providerId] ? providerId : 'google';
  const name = providerName(provider);
  // Callers wire this straight to onClick, so the "options" can be a click
  // event; only an explicit `false` forbids navigation, and only a known
  // entry id is recorded (anything else is 'other').
  const mayNavigate = useRedirectOnMobile !== false;
  const inApp = inAppBrowserName();
  // A provider not offered in this app's browser (Google in all of them) is
  // refused there. With the email link on (#3928) there is a real way in,
  // so the second tap no longer "tries anyway" into Google's refusal.
  const refusedHere = inApp && !PROVIDERS[provider].inApp.includes(inApp);
  let flow = refusedHere && (!inAppWarned || emailSignInAvailable()) ? 'in_app_browser'
    : (mayNavigate && isIOS() ? 'redirect' : 'popup');
  try {
    const firebaseProvider = PROVIDERS[provider].make();

    // `entry`: which CTA or surface started it (content/landingIds.js),
    // for the Desk's landing watch (#2512). An id, never a label.
    track(EVENTS.SIGNIN_STARTED, {
      method: provider, flow, entry: signinEntry(entry), platform: signInPlatform(),
      ...(Number.isInteger(position) ? { position } : {}), ...signInAttemptProps(),
    });

    if (flow === 'in_app_browser') {
      inAppWarned = true;
      throw Object.assign(new Error(`${name} sign-in inside the ${inApp} in-app browser`),
        { code: IN_APP_CODE, app: inApp, entry: signinEntry(entry) });
    }

    if (flow === 'redirect') {
      // The page unloads; the result is picked up on return by
      // completeGoogleRedirect(), mounted at app root.
      markSignInPending('redirect', Date.now(), provider);
      await startRedirect(provider);
      return undefined;
    }

    markSignInPending('popup', Date.now(), provider);
    let result;
    try {
      result = await signInWithPopup(auth, firebaseProvider);
    } catch (popupError) {
      // A genuinely BLOCKED popup falls back to the redirect (2026-07-30: one
      // desktop Chrome user with popups hard-blocked clicked sign-in 49 times
      // over 14 minutes and NOTHING happened). A user closing the popup is a
      // decision, rethrown below like before.
      // #2032: Chrome 142 (the build managed/school devices pin) fails the
      // popup in the SAME SECOND with auth/internal-error — 4 of 5 such
      // sessions in 30 days, zero completed sign-ins on that build. The
      // popup path is dead there; the redirect is the only one left.
      const blocked = POPUP_BLOCKED.includes(popupError.code);
      const opaque = popupError.code === 'auth/internal-error';
      if (mayNavigate && (blocked || opaque)) {
        console.info('Popup unavailable — falling back to redirect:', popupError.code);
        try { track(EVENTS.SIGNIN_POPUP_FALLBACK, { code: popupError.code }); } catch { /* never block sign-in */ }
        flow = 'redirect';
        markSignInPending('redirect', Date.now(), provider);
        await startRedirect(provider);
        return undefined;
      }
      throw popupError;
    }
    clearSignInPending();
    // The signed-in user info.
    const user = result.user;
    console.log('User signed in: ', user);
    // First-ever account creation — the acquisition conversion, fired via the
    // shared helper so popup and redirect stay identical.
    trackSignInResult(result);
    return user;
  } catch (error) {
    // The page is leaving for Google in this tab; this popup's outcome no
    // longer matters. Stay pending, like the redirect itself.
    if (handedToRedirect && flow === 'popup') return new Promise(() => {});
    clearSignInPending();
    // A closed/cancelled popup is a user action, not a failure — log it quietly
    // so the error dashboard stays for real problems. Still rethrow either way:
    // the caller shows the notice (useSignInNotice) and resets its button.
    const errorCode = error.code;
    error.provider = provider;
    // Account exists with another way in (#5154): keep the credential for
    // the consent after the student signs in the old way, and tell the
    // caller (it opens "This email already has a Stratia account").
    if (errorCode === ACCOUNT_EXISTS_CODE) {
      error.accountExists = stashPendingLink(provider, error) || { provider, email: '' };
      try { track(EVENTS.SIGNIN_ACCOUNT_EXISTS, { method: provider, existing: 'unknown', ...signInAttemptProps() }); } catch { /* never blocks */ }
    }
    // Telemetry gap closed (2026-07-29 login complaint): started→completed
    // showed desktop users retrying ~4x with ZERO fallback events — every
    // non-blocked failure was invisible. cancelled=true separates user
    // choice from real breakage; `reason` is the Desk's sign-in health
    // dimension (#2457), grouped per device there.
    try {
      track(EVENTS.SIGNIN_FAILED, {
        code: errorCode || 'unknown',
        cancelled: CANCELLED_CODES.includes(errorCode),
        reason: error?.reason === REASONS.GOOGLE_UNREACHABLE ? error.reason : failureReason(errorCode, error),
        flow,
        method: provider,
        ...(error?.app ? { app: error.app } : {}),
        ...failureDetail(error),
        ...signInAttemptProps(),
      });
    } catch { /* analytics never blocks sign-in */ }
    if (CANCELLED_CODES.includes(errorCode)) {
      console.info(`${name} sign-in cancelled by user:`, errorCode);
    } else {
      console.error(`${name} Sign-In Error:`, { errorCode, errorMessage: error.message });
    }
    throw error;
  }
};

/** Google, as every caller before #5154 knew it. */
export const signInWithGoogle = (opts = {}) => signInWithProvider('google', opts);

// "Sign in in this tab" on the popup overlay (#3483). Every Mac sign-in
// counted lost since Sep 30 was the opener page reloaded or left 2-4s after
// Google's window opened, which orphans that window; the overlay gave no
// other way out. This hands the same sign-in to the full-page redirect,
// which works on every desktop browser (authDomain is this site, so the
// redirect's state stays first-party). The popup's own promise is left to
// Firebase: the page unloads before it can matter. Never throws: a failure
// is counted, then returned so the overlay can say so.
// Set while the page is handing a pending popup over to the redirect: the
// popup's promise can still reject (popup-closed-by-user) before the page
// unloads, and that late rejection must not wipe the redirect's marker,
// count a failure or raise a notice (reviewer, #3483).
let handedToRedirect = false;
export const _resetHandoff = () => { handedToRedirect = false; };

export const continueSignInHere = async ({ waitedMs } = {}) => {
  const waited = Number.isFinite(waitedMs) ? Math.max(0, Math.round(waitedMs / 1000)) : null;
  try {
    track(EVENTS.SIGNIN_POPUP_FALLBACK, { code: POPUP_UNSEEN_CODE, ...(waited != null ? { waited_s: waited } : {}) });
  } catch { /* analytics never blocks sign-in */ }
  const provider = pendingProvider();
  markSignInPending('redirect', Date.now(), provider);
  handedToRedirect = true;
  try {
    await startRedirect(provider);
    return null;
  } catch (error) {
    handedToRedirect = false;
    clearSignInPending();
    try {
      track(EVENTS.SIGNIN_FAILED, {
        code: error?.code || 'unknown',
        cancelled: CANCELLED_CODES.includes(error?.code),
        reason: error?.reason === REASONS.GOOGLE_UNREACHABLE ? error.reason : failureReason(error?.code),
        flow: 'redirect',
        ...failureDetail(error),
        ...signInAttemptProps(),
      });
    } catch { /* analytics never blocks */ }
    return error;
  }
};

// Completes a redirect-based sign-in when the browser returns from Google.
// Returns the signed-in user (so the caller can navigate to the post-sign-in
// destination) or null when there's no pending redirect — which is the case
// for every desktop load and every normal navigation, making this a cheap
// no-op there. Never throws: a failure here must not break app load.
export const completeGoogleRedirect = async ({ onAccountExists } = {}) => {
  try {
    const result = await getRedirectResult(auth);
    if (!result || !result.user) return null;
    clearSignInPending();
    console.log('User signed in (redirect): ', result.user);
    trackSignInResult(result);
    return result.user;
  } catch (error) {
    if (error?.code === 'auth/missing-initial-state') {
      // The redirect stash didn't survive (a killed browser mid-hop, a tab
      // the Google app handed elsewhere). The pending marker stays, so
      // detectLostSignIn() counts it and shows the retry.
      console.info('Redirect state lost — the user is offered a retry.');
      try { track(EVENTS.SIGNIN_REDIRECT_STATE_LOST, {}); } catch { /* analytics never blocks */ }
      return null;
    }
    console.error('Redirect Completion Error:', {
      errorCode: error?.code,
      errorMessage: error?.message,
    });
    // The provider the redirect went to (#5154): its pending marker says.
    const provider = pendingProvider();
    // Counted under its real code, not left for detectLostSignIn to file
    // as redirect_lost (reviewer, #2457): the Desk's per-device read must
    // see what actually broke.
    clearSignInPending();
    try {
      track(EVENTS.SIGNIN_FAILED, {
        code: error?.code || 'unknown',
        cancelled: CANCELLED_CODES.includes(error?.code),
        reason: failureReason(error?.code, error),
        flow: 'redirect',
        method: provider,
        ...failureDetail(error),
        ...signInAttemptProps(),
      });
    } catch { /* analytics never blocks */ }
    // Account exists, met on the way back from Apple's page (#5154).
    if (error?.code === ACCOUNT_EXISTS_CODE) {
      const info = stashPendingLink(provider, error) || { provider, email: '' };
      try { track(EVENTS.SIGNIN_ACCOUNT_EXISTS, { method: provider, existing: 'unknown', ...signInAttemptProps() }); } catch { /* never blocks */ }
      try { onAccountExists?.(info); } catch { /* the sheet never breaks app load */ }
    }
    return null;
  }
};

// A sign-in that died mid-flight (#2457): 40 of 91 iOS starters over 90 days
// left no signin_completed and no signin_failed — the page that started the
// sign-in was gone before either could fire. Run once per page load, AFTER
// completeGoogleRedirect(): a marker left by the previous page life, with
// nobody signed in once auth has settled, is that silent failure. Counts it
// as signin_failed and returns the reason so the caller can show the retry;
// returns null when there is nothing to tell the student. Never throws.
// `waited_s` (marker to this load) and `load` (reload / back_forward /
// navigate) say how the page was left and how soon (#3483): the first Mac
// cases were only readable by stitching $pageleave rows together.
// A redirect marker on a back_forward load is the student pressing Back on
// Google's page (#3894): Firebase returns with a plain navigation, never a
// history step. Counted cancelled under its own code and not announced,
// like a closed popup. `restored`: the page came back from the back/forward
// cache without reloading (RedirectSignInHandler's pageshow), the same Back.
// `onBack`: called when that Back is what happened, so the caller can offer
// the email link to a student whose school blocks Google (#3928).
// A redirect marker read on a load of ANOTHER page than the one the sign-in
// started on (#4117) is the student coming back their own way (a re-opened
// link, a typed address): Firebase only returns to the start page. Counted
// cancelled under stratia/sign-in-left and handled like Back. When either
// path is unknown it stays "lost": that is the side that keeps a real
// failure visible.
// The provider of the last sign-in detectLostSignIn reported lost (#5154),
// so the retry goes back to the same one.
let lostProvider = 'google';
export const lastLostProvider = () => lostProvider;

export const detectLostSignIn = async ({ restored = false, onBack } = {}) => {
  try {
    const lost = takeLostSignIn(Date.now(), { restored });
    if (!lost) return null;
    const { flow, waitedMs, path, provider } = lost;
    try { await auth.authStateReady?.(); } catch { /* settle best-effort */ }
    if (auth.currentUser) return null;
    const load = restored ? 'back_forward' : pageLoadType();
    const wentBack = flow === 'redirect' && load === 'back_forward';
    const here = loadedPath();
    const cameBackElsewhere = !wentBack && flow === 'redirect' && !!path && !!here && path !== here;
    const quiet = wentBack || cameBackElsewhere;
    const reason = wentBack ? REASONS.REDIRECT_BACK
      : cameBackElsewhere ? REASONS.REDIRECT_LEFT
        : flow === 'redirect' ? REASONS.REDIRECT_LOST : REASONS.POPUP_LOST;
    try {
      track(EVENTS.SIGNIN_FAILED, {
        code: wentBack ? BACK_CODE : cameBackElsewhere ? LEFT_CODE : LOST_CODE, cancelled: quiet, reason, flow,
        method: provider,
        waited_s: Math.round(waitedMs / 1000), load,
        ...(restored ? { restored: true } : {}),
        ...signInAttemptProps(),
      });
    } catch { /* analytics never blocks */ }
    if (quiet) {
      try { onBack?.(provider); } catch { /* the offer never breaks the count */ }
      return null;
    }
    lostProvider = provider;
    return reason;
  } catch {
    return null;
  }
};

// The single, COMPLETE sign-out used by every sign-out path (the nav button
// and the idle-timeout auto sign-out). It (1) ends the Firebase session,
// (2) wipes app-local session caches, and (3) HARD-redirects to the landing
// page — a full document navigation, not an SPA route change, so every React
// context/cache from the old session is dropped. This is what fixes signing
// out sometimes leaving the app shell (Resources / Video Tours render inside
// AppLayout and are reachable signed-out) visible after logout: we always land
// on `/`. Pass { redirect: false } to end the session without navigating
// (used by tests / callers that drive their own routing).
export const logout = async ({ redirect = true } = {}) => {
  // Astra Today web push (#3756): a shared browser (a family laptop, a school
  // Chromebook) must not keep showing this student's notes after they sign
  // out. Unsubscribe this device first, while the token still works; never
  // let it block the sign-out.
  try {
    const email = auth?.currentUser?.email;
    if (email) {
      const { turnOffPush } = await import('./push');
      await Promise.race([turnOffPush(email), new Promise((r) => { setTimeout(r, 2500); })]);
    }
  } catch { /* the sign-out goes on */ }
  try {
    await signOut(auth);
    console.log('User signed out');
  } catch (error) {
    console.error('Sign Out Error:', error);
  } finally {
    try {
      localStorage.removeItem('college_counselor_session_id');
      localStorage.removeItem('chatMessages');
      localStorage.removeItem('collegeName');
      localStorage.removeItem('intendedMajor');
      // An unfinished email-link request (#3928): its email and the tab's
      // conversation must not outlive a sign-out on a shared computer.
      localStorage.removeItem(EMAIL_LINK_STORED_KEY);
    } catch { /* storage unavailable */ }
    // A pending "add Apple" (#5154) must not wait for the next person.
    clearPendingLink();
    // Reset the idle clock so a stale timestamp can't bounce the next sign-in
    // (shared/school computers).
    clearIdleActivity();
    if (redirect) window.location.assign('/');
  }
};
